/**
 * Нерешённые имена, собранные со всех карточек в одно место.
 *
 * Каждая карточка сообщает о конфликте сама — она обязана, иначе посчитает
 * не то. Но человеку восемь одинаковых жалоб «Азарова подходит нескольким»
 * не помогают: вопрос один, и задать его надо один раз, над сеткой.
 */

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { Conflict } from "../api/types";

type ConflictsStore = {
  conflicts: Conflict[];
  report: (panel: string, found: Conflict[]) => void;
};

const ConflictsContext = createContext<ConflictsStore | null>(null);

export function ConflictsProvider({ children }: { children: ReactNode }) {
  const [byPanel, setByPanel] = useState<Record<string, Conflict[]>>({});

  const report = useCallback((panel: string, found: Conflict[]) => {
    setByPanel((current) => {
      const previous = current[panel] ?? [];
      if (same(previous, found)) return current;
      return { ...current, [panel]: found };
    });
  }, []);

  const conflicts = useMemo(() => {
    const seen = new Map<string, Conflict>();
    for (const list of Object.values(byPanel)) {
      for (const conflict of list) seen.set(`${conflict.field}:${conflict.query}`, conflict);
    }
    return [...seen.values()];
  }, [byPanel]);

  const value = useMemo(() => ({ conflicts, report }), [conflicts, report]);
  return <ConflictsContext.Provider value={value}>{children}</ConflictsContext.Provider>;
}

export function useConflicts(): ConflictsStore {
  const store = useContext(ConflictsContext);
  if (!store) throw new Error("useConflicts вне ConflictsProvider");
  return store;
}

const same = (a: Conflict[], b: Conflict[]) =>
  a.length === b.length && a.every((item, index) => item.query === b[index]?.query);
